import packageInfo from '../../package.json';
import {
  getInsightResponseUpdateDate,
  isUpdateDateInCurrentPeriod,
} from './insightSummaryPeriod';
import type { IInsightSummaryResponse } from 'services/insightCasino';

/** Bound number of casino:timeframe entries kept in IndexedDB. */
export const INSIGHT_SUMMARY_CACHE_MAX_ENTRIES = 16;

/** Soft size hint for tests / diagnostics (IDB can hold multi‑MB floors). */
export const INSIGHT_SUMMARY_CACHE_LARGE_BYTES = Math.floor(1.5 * 1024 * 1024);

const APP_VERSION = packageInfo.version;
const DB_NAME = 'slotcheck-insight-summary';
const DB_VERSION = 1;
const STORE_NAME = 'summaries';
/** Legacy localStorage keys from #45 — purged on startup. */
export const LEGACY_INSIGHT_SUMMARY_LS_PREFIX = 'insightSummary:';

/** How long we wait for an IDB read before starting network-only. */
export const INSIGHT_SUMMARY_IDB_READ_BUDGET_MS = 100;

export type InsightSummaryCacheEntry = {
  /** `${appVersion}:${casinoId}:${timeframe}` */
  id: string;
  appVersion: string;
  casinoId: number;
  timeframe: number;
  updateDate: string | null;
  cachedAt: number;
  response: IInsightSummaryResponse;
};

export type InsightSummaryStoreAdapter = {
  get(id: string): Promise<InsightSummaryCacheEntry | null>;
  put(entry: InsightSummaryCacheEntry): Promise<void>;
  delete(id: string): Promise<void>;
  getAll(): Promise<InsightSummaryCacheEntry[]>;
};

function entryId(casinoId: number, timeframe: number, version: string = APP_VERSION): string {
  return `${version}:${casinoId}:${timeframe}`;
}

function isValidResponse(value: unknown): value is IInsightSummaryResponse {
  if (!value || typeof value !== 'object') return false;
  const r = value as IInsightSummaryResponse;
  return Array.isArray(r.DataSet);
}

function isValidEntry(value: unknown): value is InsightSummaryCacheEntry {
  if (!value || typeof value !== 'object') return false;
  const e = value as InsightSummaryCacheEntry;
  return (
    typeof e.id === 'string' &&
    typeof e.casinoId === 'number' &&
    typeof e.timeframe === 'number' &&
    typeof e.cachedAt === 'number' &&
    Number.isFinite(e.cachedAt) &&
    isValidResponse(e.response)
  );
}

/** Feature-detect IndexedDB (false in SSR / blocked / missing). */
export function isIndexedDbUsable(
  factory: IDBFactory | null | undefined = typeof indexedDB !== 'undefined' ? indexedDB : undefined
): boolean {
  try {
    return !!factory && typeof factory.open === 'function';
  } catch {
    return false;
  }
}

// ---------- adapter (real IDB or test double) ----------

let testAdapter: InsightSummaryStoreAdapter | null | undefined;
let idbDisabled = false;
let dbPromise: Promise<IDBDatabase | null> | null = null;

/**
 * True when a cache adapter is usable for this session (real IDB or test double).
 * When false, callers should take the sync network-only path.
 */
export function isInsightSummaryCacheAvailable(): boolean {
  if (testAdapter === null || idbDisabled) return false;
  if (testAdapter) return true;
  return isIndexedDbUsable();
}

/** Test-only: inject a store, or `null` to force “IDB unavailable”. */
export function __setInsightSummaryStoreAdapterForTests(
  adapter: InsightSummaryStoreAdapter | null | undefined
): void {
  testAdapter = adapter;
  idbDisabled = adapter === null;
  dbPromise = null;
}

export function __resetInsightSummaryIdbStateForTests(): void {
  testAdapter = undefined;
  idbDisabled = false;
  dbPromise = null;
}

function openDatabase(
  factory: IDBFactory = indexedDB
): Promise<IDBDatabase | null> {
  if (!isIndexedDbUsable(factory) || idbDisabled) return Promise.resolve(null);
  return new Promise((resolve) => {
    let req: IDBOpenDBRequest;
    try {
      req = factory.open(DB_NAME, DB_VERSION);
    } catch {
      idbDisabled = true;
      resolve(null);
      return;
    }
    req.onupgradeneeded = () => {
      try {
        const db = req.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
          store.createIndex('cachedAt', 'cachedAt', { unique: false });
          store.createIndex('appVersion', 'appVersion', { unique: false });
        }
      } catch {
        /* ignore */
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => {
      idbDisabled = true;
      resolve(null);
    };
    req.onblocked = () => {
      // Don't hang — treat as unavailable for this session.
      idbDisabled = true;
      resolve(null);
    };
  });
}

function getDb(): Promise<IDBDatabase | null> {
  if (testAdapter !== undefined) return Promise.resolve(null);
  if (!dbPromise) dbPromise = openDatabase();
  return dbPromise;
}

function idbRequest<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error || new Error('idb request failed'));
  });
}

const idbAdapter: InsightSummaryStoreAdapter = {
  async get(id) {
    const db = await getDb();
    if (!db) return null;
    try {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const result = await idbRequest(tx.objectStore(STORE_NAME).get(id));
      return isValidEntry(result) ? result : null;
    } catch {
      return null;
    }
  },
  async put(entry) {
    const db = await getDb();
    if (!db) throw new Error('idb unavailable');
    const tx = db.transaction(STORE_NAME, 'readwrite');
    await idbRequest(tx.objectStore(STORE_NAME).put(entry));
  },
  async delete(id) {
    const db = await getDb();
    if (!db) return;
    try {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      await idbRequest(tx.objectStore(STORE_NAME).delete(id));
    } catch {
      /* ignore */
    }
  },
  async getAll() {
    const db = await getDb();
    if (!db) return [];
    try {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const rows = await idbRequest(tx.objectStore(STORE_NAME).getAll());
      return (Array.isArray(rows) ? rows : []).filter(isValidEntry);
    } catch {
      return [];
    }
  },
};

function activeAdapter(): InsightSummaryStoreAdapter | null {
  if (testAdapter === null || idbDisabled) return null;
  if (testAdapter) return testAdapter;
  if (!isIndexedDbUsable()) return null;
  return idbAdapter;
}

// ---------- legacy localStorage cleanup ----------

/** Remove leftover `insightSummary:*` keys from the #45 localStorage cache. */
export function purgeLegacyInsightSummaryLocalStorage(
  storage: Storage | null = typeof window !== 'undefined' ? window.localStorage : null
): number {
  if (!storage) return 0;
  let removed = 0;
  try {
    const toRemove: string[] = [];
    for (let i = 0; i < storage.length; i++) {
      const k = storage.key(i);
      if (k && k.startsWith(LEGACY_INSIGHT_SUMMARY_LS_PREFIX)) toRemove.push(k);
    }
    for (const k of toRemove) {
      try {
        storage.removeItem(k);
        removed++;
      } catch {
        /* ignore */
      }
    }
  } catch {
    return removed;
  }
  return removed;
}

// Run once on module load (PWA startup). Best-effort; never throws.
try {
  purgeLegacyInsightSummaryLocalStorage();
} catch {
  /* ignore */
}

// ---------- public API ----------

export async function evictInsightSummaryCacheEntries(
  maxEntries: number = INSIGHT_SUMMARY_CACHE_MAX_ENTRIES
): Promise<void> {
  const adapter = activeAdapter();
  if (!adapter || maxEntries < 1) return;
  try {
    const all = await adapter.getAll();
    if (all.length <= maxEntries) return;
    const sorted = all.slice().sort((a, b) => a.cachedAt - b.cachedAt);
    const toRemove = sorted.length - maxEntries;
    for (let i = 0; i < toRemove; i++) {
      try {
        await adapter.delete(sorted[i].id);
      } catch {
        /* ignore */
      }
    }
  } catch {
    /* ignore */
  }
}

export async function readInsightSummaryCache(
  casinoId: number,
  timeframe: number
): Promise<InsightSummaryCacheEntry | null> {
  const adapter = activeAdapter();
  if (!adapter) return null;
  try {
    const entry = await adapter.get(entryId(casinoId, timeframe));
    if (!entry) return null;
    // Ignore entries from other app versions (key already scopes version).
    if (entry.appVersion && entry.appVersion !== APP_VERSION) return null;
    if (!isValidEntry(entry)) {
      try {
        await adapter.delete(entryId(casinoId, timeframe));
      } catch {
        /* ignore */
      }
      return null;
    }
    return entry;
  } catch {
    return null;
  }
}

/**
 * Persist a summary response to IndexedDB. Never throws.
 * Returns false when IDB is unavailable / write fails.
 */
export async function writeInsightSummaryCache(
  casinoId: number,
  timeframe: number,
  response: IInsightSummaryResponse,
  nowMs: number = Date.now()
): Promise<boolean> {
  const adapter = activeAdapter();
  if (!adapter || !isValidResponse(response)) return false;
  try {
    const entry: InsightSummaryCacheEntry = {
      id: entryId(casinoId, timeframe),
      appVersion: APP_VERSION,
      casinoId,
      timeframe,
      updateDate: getInsightResponseUpdateDate(response),
      cachedAt: nowMs,
      response,
    };
    await adapter.put(entry);
    await evictInsightSummaryCacheEntries(INSIGHT_SUMMARY_CACHE_MAX_ENTRIES);
    return true;
  } catch {
    // Quota / private mode / blocked: disable further IDB attempts this session.
    try {
      idbDisabled = testAdapter === undefined;
    } catch {
      /* ignore */
    }
    return false;
  }
}

/** Clear every insight summary entry (sign-out). Never throws. */
export async function clearAllInsightSummaryPersistentCache(): Promise<void> {
  const adapter = activeAdapter();
  if (!adapter) return;
  try {
    const all = await adapter.getAll();
    for (const entry of all) {
      try {
        await adapter.delete(entry.id);
      } catch {
        /* ignore */
      }
    }
  } catch {
    /* ignore */
  }
}

/** Fire-and-forget write — never blocks the emit/complete path. */
export function writeInsightSummaryCacheAsync(
  casinoId: number,
  timeframe: number,
  response: IInsightSummaryResponse,
  nowMs?: number
): void {
  try {
    void writeInsightSummaryCache(casinoId, timeframe, response, nowMs).catch(() => undefined);
  } catch {
    /* ignore */
  }
}

export async function removeInsightSummaryCache(
  casinoId: number,
  timeframe: number
): Promise<void> {
  const adapter = activeAdapter();
  if (!adapter) return;
  try {
    await adapter.delete(entryId(casinoId, timeframe));
  } catch {
    /* ignore */
  }
}

/** Cached payload is usable for the open :05/:35 period. */
export function isInsightSummaryCacheFresh(
  entry: InsightSummaryCacheEntry | null,
  now: Date = new Date()
): boolean {
  if (!entry) return false;
  const updateDate = entry.updateDate ?? getInsightResponseUpdateDate(entry.response);
  return isUpdateDateInCurrentPeriod(updateDate, now);
}

/**
 * Best-effort IDB read with a time budget. Resolves `null` on timeout /
 * unavailability so callers can proceed network-only without hanging.
 */
export function readInsightSummaryCacheWithBudget(
  casinoId: number,
  timeframe: number,
  budgetMs: number = INSIGHT_SUMMARY_IDB_READ_BUDGET_MS
): Promise<InsightSummaryCacheEntry | null> {
  return new Promise((resolve) => {
    let settled = false;
    const done = (v: InsightSummaryCacheEntry | null) => {
      if (settled) return;
      settled = true;
      resolve(v);
    };
    const timer = setTimeout(() => done(null), budgetMs);
    readInsightSummaryCache(casinoId, timeframe)
      .then((entry) => {
        clearTimeout(timer);
        done(entry);
      })
      .catch(() => {
        clearTimeout(timer);
        done(null);
      });
  });
}

/** In-memory adapter for unit tests (no real IndexedDB). */
export function createMemoryInsightSummaryStore(): InsightSummaryStoreAdapter {
  const map = new Map<string, InsightSummaryCacheEntry>();
  return {
    async get(id) {
      return map.get(id) ?? null;
    },
    async put(entry) {
      map.set(entry.id, entry);
    },
    async delete(id) {
      map.delete(id);
    },
    async getAll() {
      return Array.from(map.values());
    },
  };
}
